import get from "lodash.get";
import isEmpty from "lodash.isempty";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import {
  getCustomerReviewAPI,
  getRecentViewedByYouAPI,
  whatOthersAreViewingAPI,
  youMayAlsoLikeAPI,
} from "../../../api/pdpApi";

import Widget from "../../../components/organisms/widget";
import {
  RECENTLY_VIEWED_BY_YOU_TITLE,
  TESTIMONIAL_TITLE,
  WHAT_OTHERS_LIKE_TITLE,
  YOU_MAY_ALSO_LIKE_TITLE,
} from "../../../constants/common/common";
import { getLocalStorageItem } from "../../../utils/storage/localStorageUtil";
import { borderLine } from "../../../utils/pdp/common";
import { PageTypes } from "../../../utils/fnpPageType";
import useIntersectionObserver from "../../../customHooks/intersectionObserver";
import dynamic from "next/dynamic";
import CleverTapWidget from "../../../components/organisms/cleverTapWidget";

const CustomerReviewsCarousel = dynamic(() => import("../../../components/organisms/customerReviewsCarousel"));

/**
 * ProductDetailWidgetSection component displays a section with customer reviews and product recommendation widgets.
 * The widgets are lazy-loaded when they come into the viewport, while the customer reviews section loads immediately.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Object} props.currentItem - The current product item being viewed.
 * @param {boolean} props.isMobile - Indicates if the view is in mobile mode.
 * @param {Object} props.serverCustomerReviewData - customer review data from server side.
 * @returns {React.JSX} The rendered component.
 */
const ProductDetailWidgetSection = ({ currentItem, isMobile, serverCustomerReviewData }) => {
  const widgetRef = useRef(null);
  const [isClient, setIsClient] = useState(false);

  const isWidgetVisible = useIntersectionObserver(widgetRef, { freezeOnceVisible: true });

  const [whatOthersLikeData, setWhatOthersLikeData] = useState({});
  const [youMayAlsoLikeData, setYouMayAlsoLikeData] = useState({});
  const [recentlyViewByYouData, setRecentlyViewByYouData] = useState({});
  const [customerReviewsData, setCustomerReviewsData] = useState(serverCustomerReviewData || {});

  /**
   * Fetches data for the "What Others Like" widget.
   * @async
   * @returns {void}
   */
  const fetchWhatOthersLike = async () => {
    const response = await whatOthersAreViewingAPI(currentItem?.catalogId, false, currentItem?.productId);
    setWhatOthersLikeData(response?.data);
  };

  /**
   * Fetches data for the "You May Also Like" widget.
   * @async
   * @returns {void}
   */
  const fetchYouMayAlsoLike = async () => {
    const response = await youMayAlsoLikeAPI(13, currentItem?.productId);
    setYouMayAlsoLikeData(response?.data);
  };

  /**
   * Fetches data for the "Recently Viewed By You" widget.
   * @async
   * @returns {void}
   */
  const fetchRecentlyViewedByYou = async () => {
    const response = await getRecentViewedByYouAPI(JSON.parse(getLocalStorageItem("recentlyViewedProducts") || "[]"));
    setRecentlyViewByYouData(response?.data);
  };

  /**
   * Fetches customer reviews data.
   * @async
   * @returns {void}
   */
  const fetchCustomerReviews = async () => {
    const response = await getCustomerReviewAPI(currentItem?.productId);
    setCustomerReviewsData(response);
  };

  // Fetch customer reviews immediately on component mount or when productId changes
  useEffect(() => {
    setIsClient(true);
    if (isClient) {
      fetchCustomerReviews();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentItem?.productId]);

  // Fetch widget data only when the widget section becomes visible
  useEffect(() => {
    if (isWidgetVisible) {
      fetchYouMayAlsoLike();
      fetchWhatOthersLike();
      fetchRecentlyViewedByYou();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWidgetVisible, currentItem?.productId]);

  return (
    <div className="flex flex-col gap-20 md:gap-40 md:p-40" id="widget_section">
      {!isEmpty(get(customerReviewsData, "data")) && (
        <div>
          <CustomerReviewsCarousel
            key={`${Math.random()}`}
            reviews={get(customerReviewsData, "data")}
            pageType={typeof window !== "undefined" ? window.fnpPageType : ""}
            title={TESTIMONIAL_TITLE}
            productId={currentItem?.productId}
            totalReviews={get(customerReviewsData, "total")}
            isMobile={isMobile}
            viewAllStyle="pl-16 pr-16 md:pl-0"
          />
          {borderLine()}
        </div>
      )}
      <CleverTapWidget fromPage={PageTypes?.PRODUCT} skuCode={currentItem?.skuCode} isMobile={isMobile} />
      <div ref={widgetRef}>
        {isWidgetVisible && (
          <>
            {recentlyViewByYouData?.productResults?.length > 0 && (
              <div>
                <Widget
                  widgetTitle={RECENTLY_VIEWED_BY_YOU_TITLE}
                  productsList={get(recentlyViewByYouData, "productResults", [])}
                  isMobile={isMobile}
                  type="RVBY"
                  position={1}
                  skuCode={currentItem?.skuCode}
                  pageType={PageTypes?.PRODUCT}
                  titleStyle="pl-0"
                />
                {borderLine()}
              </div>
            )}

            {youMayAlsoLikeData?.productResults?.length > 0 && (
              <div className="mt-20 md:mt-40">
                <Widget
                  widgetTitle={YOU_MAY_ALSO_LIKE_TITLE}
                  productsList={get(youMayAlsoLikeData, "productResults", [])}
                  isMobile={isMobile}
                  type="YMAL"
                  position={2}
                  skuCode={currentItem?.skuCode}
                  pageType={PageTypes?.PRODUCT}
                  titleStyle="pl-0"
                />
                {borderLine()}
              </div>
            )}

            {whatOthersLikeData?.productResults?.length > 0 && (
              <div className="mt-20 md:mt-40">
                <Widget
                  widgetTitle={WHAT_OTHERS_LIKE_TITLE}
                  productsList={get(whatOthersLikeData, "productResults", [])}
                  isMobile={isMobile}
                  type="WOAV"
                  position={3}
                  skuCode={currentItem?.skuCode}
                  pageType={PageTypes?.PRODUCT}
                  titleStyle="pl-0"
                />
                {borderLine()}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

ProductDetailWidgetSection.propTypes = {
  currentItem: PropTypes.object.isRequired,
  serverCustomerReviewData: PropTypes.object.isRequired,
  isMobile: PropTypes.bool.isRequired,
};

export default ProductDetailWidgetSection;
